import { useCallback, useEffect, useState } from "react";
import type {
  FlowPatientCalendar,
  FlowSurface,
  JourneyCalendarEntry,
  JourneySummary,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { UnauthorizedError } from "@/lib/clinician-api";
import {
  attachJourneyToPatient,
  detachPatientJourney,
  fetchPatientCalendar,
  listJourneys,
} from "@/lib/journeys-api";
import { useReadOnly } from "./SupportView";

// TES-224 — the per-patient journey panel on the clinician patient page: attach
// / detach journeys and a 30-day calendar of what fires each day (grouped by
// date, each entry tagged with its journey + flow). Sits beside PatientFlows.

const SURFACE_LABELS: Record<FlowSurface, string> = {
  home_greeting: "Hem: Hälsning",
  home_now: "Hem: Just nu",
  home_for_you: "Hem: För dig idag",
  home_weekly_checkin: "Hem: Veckoincheckning",
  home_wellness_preview: "Hem: Din plan",
  // TES-866 — named apart from "Hem: För dig idag" above on purpose: canon
  // says the question section must NOT be that strip, and two similar labels
  // in one picker is how an author places it on the wrong one.
  home_questions: "Hem: Frågesektion",
  task_list: "Stegslista",
  lab_results: "Provsvar",
  health_history: "Hälsohistorik",
  wellness_program: "Wellnessprogram",
  feedback_card: "Feedback-kort",
  coach_chat: "Coach-chatt",
  doctor_chat: "Läkarchatt",
  push: "Push",
  sms: "SMS",
  email: "E-post",
  home_card: "Hemkort",
};

function entryTitle(entry: JourneyCalendarEntry): string {
  const c = entry.content;
  return (
    c.home?.title.sv ||
    c.push?.title.sv ||
    c.email?.subject.sv ||
    "(utan titel)"
  );
}

export function PatientJourneys({ patientId }: { patientId: string }) {
  const { signOut } = useAuth();
  // TES-1040 — no attaching or detaching in the admin's read-only view.
  const readOnly = useReadOnly();
  const [data, setData] = useState<FlowPatientCalendar | null>(null);
  const [published, setPublished] = useState<JourneySummary[]>([]);
  const [selected, setSelected] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleError = useCallback(
    (e: unknown) => {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    },
    [signOut],
  );

  const reload = useCallback(() => {
    fetchPatientCalendar(patientId).then(setData).catch(handleError);
  }, [patientId, handleError]);

  useEffect(() => {
    reload();
    listJourneys()
      .then((rows) =>
        setPublished(rows.filter((r) => r.status === "published")),
      )
      .catch(handleError);
  }, [reload, handleError]);

  async function onAttach() {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      await attachJourneyToPatient(patientId, selected);
      setSelected("");
      reload();
    } catch (e) {
      handleError(e);
    } finally {
      setBusy(false);
    }
  }

  async function onDetach(journeyAssignmentId: string) {
    setBusy(true);
    setError(null);
    try {
      await detachPatientJourney(patientId, journeyAssignmentId);
      reload();
    } catch (e) {
      handleError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-md">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg">Resor</h2>
        {readOnly ? null : (
          <div className="flex items-center gap-sm">
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="rounded-md border border-surfaceMuted bg-surface px-sm py-xs text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
            >
              <option value="">Välj resa…</option>
              {published.map((j) => (
                <option key={j.key} value={j.key}>
                  {j.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={onAttach}
              disabled={!selected || busy}
              className="px-md py-xs rounded-md bg-brand text-white text-sm hover:opacity-90 disabled:opacity-50"
            >
              Koppla
            </button>
          </div>
        )}
      </div>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-sm text-sm">
          {error}
        </div>
      ) : null}

      {/* Active journeys */}
      {data && data.journeys.length > 0 ? (
        <div className="flex flex-col gap-xs">
          <h3 className="text-xs font-semibold text-textSecondary uppercase tracking-wide">
            Aktiva resor
          </h3>
          {data.journeys.map((j) => (
            <div
              key={j.id}
              className="flex items-center justify-between border-b border-surfaceMuted last:border-b-0 py-xs text-sm"
            >
              <span>
                {j.journeyName}{" "}
                <span className="text-textSecondary text-xs">
                  (v{j.journeyVersion} •{" "}
                  {j.source === "manual" ? "manuell" : "händelse"})
                </span>
              </span>
              {readOnly ? null : (
                <button
                  type="button"
                  onClick={() => onDetach(j.id)}
                  disabled={busy}
                  className="text-xs text-textSecondary hover:text-danger disabled:opacity-50"
                >
                  Koppla bort
                </button>
              )}
            </div>
          ))}
        </div>
      ) : null}

      {/* Calendar */}
      <div className="flex flex-col gap-sm">
        <h3 className="text-xs font-semibold text-textSecondary uppercase tracking-wide">
          Kalender (30 dagar)
        </h3>
        {!data ? (
          <p className="text-textSecondary text-sm">Hämtar…</p>
        ) : data.calendar.length === 0 ? (
          <p className="text-textSecondary text-sm">
            Inget schemalagt. Koppla en resa eller ett flöde för att börja.
          </p>
        ) : (
          data.calendar.map((day) => (
            <div key={day.dueOn} className="flex flex-col gap-xs">
              <div className="text-xs font-medium text-textPrimary">
                {day.dueOn}
              </div>
              {day.entries.map((entry) => (
                <div
                  key={entry.id}
                  className="flex items-center justify-between gap-md border border-surfaceMuted rounded-md px-sm py-xs text-sm"
                >
                  <div className="flex items-center gap-sm min-w-0">
                    {entry.timeOfDay ? (
                      <span className="text-textSecondary text-xs tabular-nums">
                        {entry.timeOfDay}
                      </span>
                    ) : null}
                    <span className="truncate">{entryTitle(entry)}</span>
                    <span className="flex gap-xs shrink-0">
                      {entry.surfaces.map((s) => (
                        <span
                          key={s}
                          className="px-xs py-[1px] rounded-pill text-[10px] font-medium bg-surfaceMuted text-textSecondary"
                        >
                          {SURFACE_LABELS[s]}
                        </span>
                      ))}
                    </span>
                  </div>
                  <span className="text-textSecondary text-xs shrink-0 hidden sm:inline">
                    {entry.journeyName ? `${entry.journeyName} · ` : ""}
                    {entry.flowName}
                  </span>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </section>
  );
}
